import { generateNKeysBetween } from "fractional-indexing";
import type { GraphQLClient } from "@package/graphql";
import { logger } from "@artis/logger";

import type { PageFragmentFragment, PageMemberFragmentFragment } from "../../__generated__/gql/graphql";
import { PageMemberUpdateMutation } from "./queries";

export const sort = (
  userId: string,
  pages: readonly PageFragmentFragment[],
  pageMembers: readonly PageMemberFragmentFragment[] | { page_id: string; user_id: string; page_order: string }[],
  hasura: GraphQLClient,
) => {
  let hasPlaceholder = false;
  const sorted = pages
    .map((page) => {
      const members = pageMembers.filter((pm) => pm.page_id === page.id);
      const shared = members.length > 1;
      const member = pageMembers.find((pm) => pm.page_id === page.id && pm.user_id === userId);

      if (member?.page_order === "placeholder") {
        hasPlaceholder = true;
      }

      return member ? { ...page, idx: member.page_order, shared } : { ...page, shared: false };
    })
    .sort((a, b) => (a.idx < b.idx ? -1 : 1));

  // If there are no placeholder idxs, return early.
  if (!hasPlaceholder) {
    return sorted;
  }

  // When a page is shared with another user, we set a temporary "placeholder" idx to avoid conflicts since the user sharing the page does not know the recipient's page order. The recipient's client will then generate a proper idx for the page based on their current page order. This avoids potential security concerns with using a Netlify function to fetch and update the recipient's page order with a global Hasura client etc. and keeps the logic on the client side (it's cheap anyway).
  try {
    const filtered = sorted.filter((p) => p.idx !== "placeholder");
    const placeholders = sorted.filter((p) => p.idx === "placeholder");
    const lastPage = filtered[filtered.length - 1];
    const lastIdx = lastPage ? lastPage.idx : null;
    const generatedIdxs = generateNKeysBetween(lastIdx, null, placeholders.length);

    const updatedPlaceholders = placeholders.map((p, i) => {
      logger.info("Updating placeholder page idx", { pageId: p.id, newIdx: generatedIdxs[i] });
      hasura.execute(PageMemberUpdateMutation, {
        userId,
        pageId: p.id,
        pageOrder: generatedIdxs[i],
      });
      return { ...p, idx: generatedIdxs[i] };
    });

    return [...filtered, ...updatedPlaceholders].sort((a, b) => (a.idx < b.idx ? -1 : 1));
  } catch (error) {
    logger.error("Error sorting pages", { error });
    return sorted;
  }
};
